import find from 'lodash/find'
import styled from 'styled-components'
import { P } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'

import { FAREWILL_LEGAL_SERVICES } from 'utils/enums'
import {
  getFullName,
  getFrontLink,
  formatDateTimeToString,
} from 'utils/helpers'
import ExternalLink from 'components/external-link'
import { Contact } from 'lib/models/contact'
import { Task } from 'lib/models/task'
import { FuneralAttributes } from 'lib/models/funeral'
import { ProbateCaseAttributes } from 'lib/models/probate-case'
import { WillCaseAttributes } from 'lib/models/will-case'
import ENV from 'config/environment'

const StyledLink = styled(ExternalLink)`
  position: relative;

  &:not(:last-of-type) {
    padding-right: ${GTR.XS};
    margin-right: ${GTR.XS};

    &:after {
      content: '•';
      position: absolute;
      right: 0;
      transform: translateX(50%);
    }
  }
`

const getZendeskSearchLink = (email: Contact['email']) => {
  const query = `email:${email}`
  return `https://farewill.zendesk.com/agent/search/1?q=${encodeURI(query)}`
}

export const getExizentCaseLink = (exizentCaseId: string) => {
  return `${ENV.EXIZENT_LEGAL_URL}/Case/Dashboard/${encodeURI(exizentCaseId)}`
}

const CLIO_MATTERS_BASE_URL = 'https://eu.app.clio.com/matters'
const getClioMatterLink = (matterId: string) =>
  `${CLIO_MATTERS_BASE_URL}/${encodeURIComponent(matterId)}`

const getWillGoogleDriveSearchLink = (
  primaryContact: Contact,
  reference: string
) => {
  const title = `(title: "${getFullName(
    primaryContact
  )}" OR title: "${reference}")`
  const query = `${title} type:folder`
  return `https://drive.google.com/drive/u/0/search?ths=true&q=${encodeURI(
    query
  )}`
}

const getFuneralGoogleDriveSearchLink = (reference: string) => {
  const query = `title: "${reference}" type:folder parent:1-z80jOv3S0aiB-aWcHhDMH67WTf6zH_m`
  return `https://drive.google.com/drive/u/0/search?ths=true&q=${encodeURI(
    query
  )}`
}

type ExternalLinks = { href: string; text: string; title?: string }[]

const externalLinks = (links: ExternalLinks) => {
  return (
    <P size="S">
      {links.map(({ href, text, title }) => (
        <StyledLink href={href} title={title} key={text}>
          {text}
        </StyledLink>
      ))}
    </P>
  )
}

export const ProbateExternalLinks = ({
  attributes,
}: {
  attributes: ProbateCaseAttributes
}) => {
  const {
    referredTo,
    contact,
    externalExizentCaseId,
    externalClioMatterId,
    externalClioSyncedAt,
  } = attributes

  const isReferredToFWLS = referredTo === FAREWILL_LEGAL_SERVICES

  const email = contact?.email

  const frontLink = email ? [{ href: getFrontLink(email), text: 'Front' }] : []

  const zendeskLink =
    email && !isReferredToFWLS
      ? [{ href: getZendeskSearchLink(email), text: 'Zendesk' }]
      : []

  const exizentCaseLink =
    isReferredToFWLS && externalExizentCaseId
      ? [{ href: getExizentCaseLink(externalExizentCaseId), text: 'Exizent' }]
      : []

  const clioSyncedAt =
    externalClioMatterId && externalClioSyncedAt
      ? formatDateTimeToString(externalClioSyncedAt)?.replace('T', ' at ')
      : null

  const clioMatterLink = externalClioMatterId
    ? [
        {
          href: getClioMatterLink(externalClioMatterId),
          text: 'Clio',
          title: clioSyncedAt ? `Clio last synced ${clioSyncedAt}` : undefined,
        },
      ]
    : []

  const links: ExternalLinks = [
    ...frontLink,
    ...zendeskLink,
    ...exizentCaseLink,
    ...clioMatterLink,
  ]

  return externalLinks(links)
}

export const FuneralExternalLinks = ({
  attributes,
  tasks,
}: {
  attributes: FuneralAttributes
  tasks?: Task[]
}) => {
  const { contact, reference } = attributes

  const isFactFindCompleted = !!find(tasks, (task) => {
    return task.attributes.coreTask === 'fact_find_completed'
  })
  const email = contact?.email

  const links = []

  if (isFactFindCompleted && reference) {
    links.push({
      href: getFuneralGoogleDriveSearchLink(reference),
      text: 'Google Drive',
    })
  }

  if (email) {
    links.push({
      href: getFrontLink(email),
      text: 'Front',
    })
  }

  if (links.length === 0) {
    return null
  }

  return externalLinks(links)
}

export const WillExternalLinks = ({
  attributes,
}: {
  attributes: WillCaseAttributes
}) => {
  const { primaryContact, reference } = attributes

  const email = primaryContact?.email

  let links =
    primaryContact && reference
      ? [
          {
            href: getWillGoogleDriveSearchLink(primaryContact, reference),
            text: 'GDrive',
          },
        ]
      : []

  if (email) {
    links = [
      {
        href: getZendeskSearchLink(email),
        text: 'Zendesk',
      },
      ...links,
    ]
  }

  return externalLinks(links)
}
